import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {
  AlertProps,
  FlagProps,
  KeyboardShortcut,
  Parameter,
  showNotification,
} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, defaultTo, isEmpty, isNil} from 'ramda';
import {concatAll, isFalse, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetReceiveNoteFastTrackItemsQuery,
  useGetReceiveNoteStateQuery,
  useGetReceiveNoteInfoQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  DetailTemplate,
  DetailTemplateHeader,
  EntityHeaderAction,
  getUserName,
  handleApiError,
  NavigationItem,
  createHeaderActions,
  Section,
  useChecklist,
  usePermissions,
  printPDF,
  useFileNotification,
} from '@dms/shared';
import {createHeaderFlags} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';
import {isReceiveNoteFastTracksPending} from './utils/isReceiveNoteFastTracksPending';

// eslint-disable-next-line complexity, max-statements -- Pre-existing violation
export function ReceiveNoteDetail() {
  const navigate = useNavigate();

  const {receiveNoteId} = useWarehouseParams();

  const fileNotification = useFileNotification({onTimeout: () => showNotification.error()});

  const dateTimeFormatter = useDateTimeFormatter();

  const isFastTrackFeatureFlagEnabled = isFeatureEnabled(
    featureFlags.AFS_WAREHOUSE_FAST_TRACK_ISSUE
  );

  const [canEditReceiveNote] = usePermissions({
    permissionKeys: ['receiveNoteEdit'],
  });

  const [canCompleteFastTrack] = usePermissions({
    permissionKeys: ['fastTrackComplete'],
  });

  const {
    data: receiveNoteState,
    isLoading: isReceiveNoteStateLoading,
    isFetching: isReceiveNoteStateFetching,
    isError: hasReceiveNoteStateError,
  } = useGetReceiveNoteStateQuery({receiveNoteId}, {skip: isNilOrEmpty(receiveNoteId)});

  const {
    data: receiveNoteInfo,
    isLoading: isReceiveNoteInfoLoading,
    isError: hasReceiveNoteInfoError,
  } = useGetReceiveNoteInfoQuery(
    {receiveNoteId},
    {
      skip: isNilOrEmpty(receiveNoteId),
      selectFromResult: ({data, isLoading, isError}) => ({
        data: data
          ? {
              markers: data.markers,
              createdBy: data.createdBy,
              updatedBy: data.updatedBy,
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            }
          : undefined,
        isLoading,
        isError,
      }),
    }
  );

  const {data: createdByUser, isLoading: isCreatedByUserLoading} = useGetUserQuery(
    {id: receiveNoteInfo?.createdBy ?? ''},
    {skip: isNilOrEmpty(receiveNoteInfo?.createdBy)}
  );

  const {data: updatedByUser, isLoading: isUpdatedByUserLoading} = useGetUserQuery(
    {id: receiveNoteInfo?.updatedBy ?? ''},
    {skip: isNilOrEmpty(receiveNoteInfo?.updatedBy)}
  );

  const {
    data: receiveNoteAction,
    isLoading: isReceiveNoteActionsLoading,
    isError: hasReceiveNoteActionError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    {id: receiveNoteId, resource: 'receive-note'},
    {skip: isNilOrEmpty(receiveNoteId)}
  );

  const {
    data: fastTrackItems,
    isLoading: isReceiveNoteFastTracksLoading,
    isError: isReceiveNoteFastTracksError,
  } = useGetReceiveNoteFastTrackItemsQuery(
    {
      receiveNoteId,
    },
    {skip: isNilOrEmpty(receiveNoteId) || isFalse(isFastTrackFeatureFlagEnabled)}
  );

  const isPending = isReceiveNoteFastTracksPending(fastTrackItems);

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-receipt-note', contextId: receiveNoteId},
    {skip: !receiveNoteId}
  );

  const [putReceiveNoteAction, {isLoading: isReceiveNoteActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'warehouse-receipt-note',
    contextId: receiveNoteId,
  });

  const handleDiscard = () => {
    navigate(warehouseRoutes.receiveNoteList);
  };

  const handleActionClick = (actionKey: string) => {
    const navigatePaths: Record<string, string | undefined> = {
      corrective: composePath(warehouseRoutes.receiveNoteCorrectionCreate, {
        params: {receiveNoteCreateId: receiveNoteId},
        isSearchParamsPreserved: false,
      }),
      complete: warehouseRoutes.receiveNoteList,
      delete: warehouseRoutes.receiveNoteList,
    };

    const handleSuccess = () => {
      const path = navigatePaths[actionKey];
      if (path) {
        // Non-empty options force same-tab navigation: these actions can be
        // triggered by the Ctrl/⌘+Enter chord, and while the modifier is held
        // useNavigate would otherwise window.open() the path in a new tab.
        navigate(path, {replace: false});
      }
    };

    if (actionKey === 'corrective') {
      handleSuccess();
      return;
    }

    if (actionKey === 'complete' && isFastTrackFeatureFlagEnabled) {
      const hasFastTrackIssues = isNotNilOrEmpty(fastTrackItems?.itemIds);

      if (hasFastTrackIssues && !canCompleteFastTrack) {
        return showNotification.warning(
          'entity.warehouse.labels.missingFastTrackCompletePermission'
        );
      }

      if (isPending) {
        return showNotification.warning('entity.warehouse.labels.receiveNotePleaseAcceptAllItems');
      }
    }

    if (actionKey === 'prntReceiveNote') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: receiveNoteId,
          originEntityPrintoutType: 'prntReceiveNote',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
              onError: () => showNotification.error(),
            });
          } else {
            showNotification.error();
          }
        })
        .then(refreshDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.receiveNoteDetailDocuments, {
              params: {id: receiveNoteId},
            })
          );
        })
        .catch(handleApiError);
    }

    // Returning the promise keeps the destructive-action confirmation dialog
    // open with a loading state until the request settles
    return putReceiveNoteAction({
      id: receiveNoteId,
      resource: 'receive-note',
      body: {actionKey},
    })
      .unwrap()
      .then(() => handleSuccess())
      .then(refreshDocumentsCount)
      .catch(handleApiError);
  };

  const hasCorrectiveAction = defaultTo([], receiveNoteAction?.actions?.options).some(
    (option) => option.key === 'corrective' && option.style !== 'hidden'
  );

  const headerActions = createHeaderActions({
    actions: receiveNoteAction?.actions,
    callback: handleActionClick,
    isLoading: isReceiveNoteActionLoading || isPostPrintoutOnDemandLoading,
    // When corrective is available it must own the confirm chord regardless of
    // backend action ordering. In other states the default applies (rightmost
    // primary button — for destructive keys the chord opens the confirmation
    // dialog, never the action itself).
    keyboardShortcuts: hasCorrectiveAction ? {corrective: KeyboardShortcut.ModEnter} : undefined,
  });

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const isLoading =
    isReceiveNoteStateLoading ||
    isReceiveNoteInfoLoading ||
    isCreatedByUserLoading ||
    isReceiveNoteActionsLoading ||
    isReceiveNoteFastTracksLoading ||
    isUpdatedByUserLoading;

  const hasError =
    hasReceiveNoteStateError ||
    hasReceiveNoteInfoError ||
    hasReceiveNoteActionError ||
    isReceiveNoteFastTracksError;

  const isDraftReceiveNote = isNil(receiveNoteState);
  const isCompletedReceiveNote = receiveNoteState?.state === 'COMPLETED';

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUser) ?? EMPTY_PLACEHOLDER}`;
  const createdAt = `${i18n.t('general.labels.createdAt')}: ${formatToShortDate(receiveNoteInfo?.createdAt)}`;

  const updatedBy = `${i18n.t('general.labels.updatedBy')}: ${getUserName(updatedByUser) ?? EMPTY_PLACEHOLDER}`;
  const updatedAt = `${i18n.t('general.labels.updatedAt')}: ${formatToShortDate(receiveNoteInfo?.updatedAt ?? undefined)}`;

  const completedBy = `${i18n.t('general.labels.completedBy')}: ${isCompletedReceiveNote ? getUserName(updatedByUser) : EMPTY_PLACEHOLDER}`;
  const completedAt = `${i18n.t('general.labels.completedAt')}: ${isCompletedReceiveNote ? formatToShortDate(receiveNoteInfo?.updatedAt ?? undefined) : EMPTY_PLACEHOLDER}`;

  const headerTitle = isEmpty(receiveNoteId)
    ? i18n.t('entity.warehouse.labels.newReceiveNote')
    : `${i18n.t('entity.warehouse.labels.receiveNote')} ${defaultTo('', receiveNoteState?.receiveNoteNumber)}`;

  const headerParameters = match(isDraftReceiveNote)
    .returnType<Parameter[]>()
    .with(true, always([]))
    .otherwise(always([createdBy, createdAt, updatedBy, updatedAt, completedBy, completedAt]));

  const markersFlags = createHeaderFlags(receiveNoteInfo?.markers);

  const staticFlags = match(receiveNoteState?.state)
    .returnType<FlagProps[]>()
    .with('PENDING', () => [{colorScheme: 'blue', label: i18n.t('general.labels.pending')}])
    .with('PROCESSING', () => [
      {colorScheme: 'neutral', label: i18n.t('general.labels.processing')},
    ])
    .with('COMPLETED', () => [{colorScheme: 'green', label: i18n.t('general.labels.completed')}])
    .otherwise(() => [{colorScheme: 'neutral', label: i18n.t('entity.warehouse.labels.draft')}]);

  const typeFlags = buildArray<FlagProps>().when(receiveNoteState?.type === 'CORE_EXCHANGE', {
    colorScheme: 'purple',
    label: i18n.t('entity.warehouse.labels.coreExchange'),
  });

  const headerFlags = concatAll<FlagProps[]>([staticFlags, typeFlags, markersFlags]);

  const staticActions = buildArray<EntityHeaderAction>().when(isDraftReceiveNote, {
    type: 'button',
    onClick: handleDiscard,
    title: i18n.t('general.actions.discard'),
    variant: 'secondary',
    'data-testid': testIds.warehouse.receiveNoteDetailOverview('headerActions.discard'),
  });

  const alerts = buildArray<AlertProps>()
    .whenNot(canEditReceiveNote, {
      type: 'banner',
      variant: 'info',
      message: i18n.t('module.warehouse.label.missingReceiveNoteEditPermission'),
    })
    .when(isPending && isFastTrackFeatureFlagEnabled, {
      type: 'banner',
      variant: 'warning',
      message: i18n.t('module.warehouse.label.receiveNoteFastTrackIssues'),
    });

  const header: DetailTemplateHeader = {
    title: headerTitle,
    isEntityLoading: isReceiveNoteStateFetching,
    icon: 'action/deployed',
    parameters: headerParameters,
    recordId: receiveNoteState?.receiveNoteId,
    resourceId: EntityResourceIds.receiveNote,
    flags: headerFlags,
    actions: [...headerActions, ...staticActions],
    controls: ['ASSIGNEE'],
    alerts,
  };

  const receiveNoteDetailHref = isNotNilOrEmpty(receiveNoteId)
    ? composePath(warehouseRoutes.receiveNoteDetailOverview, {
        params: {id: receiveNoteId},
      })
    : warehouseRoutes.receiveNoteCreate;

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: receiveNoteId || '',
    resourceId: 'RECEIVE_NOTE',
  });

  const navigation = buildArray<NavigationItem>([])
    .add({
      id: 'receiveNoteDetail',
      label: i18n.t('page.warehouse.labels.overview'),
      href: receiveNoteDetailHref,
      content: (
        <Overview
          receiveNoteId={receiveNoteId}
          isReceiveNoteLoading={isReceiveNoteStateLoading}
          hasReceiveNoteError={hasReceiveNoteStateError}
          data-testid={testIds.warehouse.receiveNoteDetailOverview('sections.overview')}
        />
      ),
      'data-testid': testIds.warehouse.receiveNoteDetailOverview('navigation.overview'),
    })
    .when(isNotNilOrEmpty(receiveNoteId), {
      id: 'receiveNoteDocuments',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.receiveNoteDetailDocuments, {
        params: {id: receiveNoteId},
      }),
      content: (
        <Documents
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.receiveNoteDetailDocuments('sections.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.receiveNoteDetailDocuments('navigation.documents'),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: 'receiveNoteChecklists',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(warehouseRoutes.receiveNoteDetailChecklists, {
        params: {id: receiveNoteId},
      }),
      content: (
        <Section>
          <Checklists
            {...checklistSettings}
            contextTarget="warehouse-receipt-note"
            contextId={receiveNoteId}
          />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.warehouse.receiveNoteDetailChecklists('checklists'),
    });

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.receiveNote')} />
      <DetailTemplate
        key={generateHashFromObjects(receiveNoteState)}
        isLoading={isLoading}
        isError={hasError}
        isLocked={isReceiveNoteActionLoading}
        header={header}
        navigation={navigation}
        data-testid={testIds.warehouse.receiveNoteDetailOverview('page')}
      />
    </>
  );
}
